import { useCallback, useEffect, useEffectEvent, useState } from 'react'

/**
 * Starts a one-shot timer that flips `hasElapsed` after `ms`
 * milliseconds. Fires an optional `onElapsed` callback exactly
 * once when the timer completes. Cleans up automatically on
 * unmount.
 */
export function useTimeout(ms: number, onElapsed?: () => void) {
  const [state, setState] = useState<{ status: 'idle' | 'pending' | 'elapsed' }>({ status: 'idle' })
  const handleElapsed = useEffectEvent(() => onElapsed?.())

  useEffect(() => {
    if (state.status !== 'pending') return

    const timer = setTimeout(() => {
      setState({ status: 'elapsed' })
      handleElapsed()
    }, ms)

    return () => clearTimeout(timer)
  }, [state, ms])

  const start = useCallback(() => {
    setState({ status: 'pending' })
  }, [])

  return {
    start,
    isPending: state.status === 'pending',
    hasElapsed: state.status === 'elapsed',
  }
}
